/*
 * From https://www.redblobgames.com/plotter/1925-delaunator-animation/
 * Copyright 2022 Red Blob Games <redblobgames@gmail.com>
 * @license Apache-2.0 <https://www.apache.org/licenses/LICENSE-2.0.html>
 */

console.info("I'm happy to answer questions about the code; email me at redblobgames@gmail.com");


import TriangleMesh from '@redblobgames/dual-mesh';
import MeshBuilder from '@redblobgames/dual-mesh/create';
import PoissonDiskSampling from 'poisson-disk-sampling';
import {setUpImageDrop} from './image-load.js';

const MM_PER_INCH = 25.4;
const AXIDRAW_PER_MM = 80;
const PAPER_IN = {w: 11, h: 8.5};
const MARGIN_IN = {w: 0.5, h: 0.5};
const OUTER = {
    w: PAPER_IN.w * MM_PER_INCH * AXIDRAW_PER_MM,
    h: PAPER_IN.h * MM_PER_INCH * AXIDRAW_PER_MM
};
const INNER = {
    left: MARGIN_IN.w * MM_PER_INCH * AXIDRAW_PER_MM,
    top: MARGIN_IN.h * MM_PER_INCH * AXIDRAW_PER_MM,
    w: OUTER.w - 2*MARGIN_IN.w * MM_PER_INCH * AXIDRAW_PER_MM,
    h: OUTER.h - 2*MARGIN_IN.h * MM_PER_INCH * AXIDRAW_PER_MM
};

// const PLOT_SCALE = 20.32;
const PLOT_SCALE = 10;

const parameters = {
    width: Math.round(INNER.w / PLOT_SCALE),
    height: Math.round(INNER.h / PLOT_SCALE),
    spacing: 20,
};


let points = new Array(1);


let pixelToRadius = (nx, ny) => {
    let radius = Math.hypot(nx, ny);
    let angle = Math.atan2(ny, nx);
    return radius < 0.8 - 0.15 * Math.sin(5 * angle) ? 0.1 : 2.0;
};

setUpImageDrop("#plot", (image) => {
    let canvas = document.createElement('canvas');
    let w = canvas.width = image.naturalWidth || image.width;
    let h = canvas.height = image.naturalHeight || image.height;
    let ctx = canvas.getContext('2d') as CanvasRenderingContext2D;
    ctx.drawImage(image, 0, 0);

    let rgba = ctx.getImageData(0, 0, w, h).data;
    function getPixel(nx, ny) { // where nx,ny between -1 and +1
        let ix = ((nx + 1) / 2 * w) | 0;
        if (ix < 0) { ix = 0; } else if (ix >= w) { ix = w-1; }
        let iy = ((ny + 1) / 2 * h) | 0;
        if (iy < 0) { iy = 0; } else if (iy >= h) { iy = h-1; }
        
        let i = 4 * (iy * w + ix);
        let r = rgba[i], g = rgba[i+1], b = rgba[i+2];
        return [r, g, b];
    }
    pixelToRadius = (nx, ny) => {
        let [r, g, b] = getPixel(nx, ny);
        let luminance = 0.2 * r + 0.7 * g + 0.1 * b;
        luminance /= 255.0;
        return luminance < 0.5 ? 0.02 : 2.0; // TODO: support in between values
    };
    draw();
});

function draw() {
    let poisson = new PoissonDiskSampling({
        shape: [parameters.width, parameters.height],
        minDistance: parameters.spacing,
        distanceFunction(p) {
            let nx = 2 * (p[0]/parameters.width - 0.5);
            let ny = 2 * (p[1]/parameters.height - 0.5);
            return pixelToRadius(nx, ny);
        },
        tries: 10,
    });
    points = poisson.fill();

    const B = Math.ceil(Math.max(parameters.width, parameters.height)/parameters.spacing);
    const boundaryPoints = new Array();
    for (let i = 0; i <= B; i++) {
        let t = i / B;
        let offset = 15 * (1 - Math.pow(t - 0.5, 2));
        let x = parameters.width * t;
        let y = parameters.height * t;
        boundaryPoints.push([-offset, y], [parameters.width+offset, y]);
        boundaryPoints.push([x, -offset], [x, parameters.height+offset]);
    }

    let builder = new MeshBuilder();
    builder.addPoints(boundaryPoints);
    builder.numBoundaryRegions = boundaryPoints.length;
    builder.addPoints(points);
    const mesh: TriangleMesh = builder.create();


    function midpoint(s) {
        let t1 = mesh.s_inner_t(s),
        t2 = mesh.s_outer_t(s);
        return [0.5*(mesh.t_x(t1) + mesh.t_x(t2)),
                0.5*(mesh.t_y(t1) + mesh.t_y(t2))];
    }




    let svg = `<svg viewBox="0 0 ${OUTER.w} ${OUTER.h}" width="11in" height="8.5in" xmlns="http://www.w3.org/2000/svg">
<g transform="translate(${INNER.left},${INNER.top}) scale(${PLOT_SCALE})">\n`


    let out_s = [], out_t = [];
    for (let r = 0; r < mesh.numSolidRegions; r++) {
        mesh.r_circulate_s(out_s, r);
        if (out_s.some(s => s < 0 || mesh.s_opposite_s(s) < 0)) { continue; /* boundary */ }
        if (r < mesh.numBoundaryRegions) { continue; /* boundary */ }

        let d = `M ${midpoint(out_s[0])}`;
        for (let i = 1; i <= out_s.length; i++) {
            let s = out_s[i % out_s.length];
            let t = mesh.s_inner_t(s);
            let p = midpoint(s);
            let q = mesh.t_pos([], t);
            q[0] = q[0]|0;
            q[1] = q[1]|0;
            p[0] = p[0]|0;
            p[1] = p[1]|0;
            d += ` Q ${q} ${p}`;
        }
        svg += `\n   <path d="${d}" fill="none" stroke="black" stroke-width="1"/>`;
    }


    svg += `\n\n</g></svg>`;

    (document.querySelector("#plot") as HTMLElement).innerHTML = svg;
    (document.querySelector("main textarea") as HTMLTextAreaElement).value = `<?xml version="1.0" encoding="UTF-8" standalone="no"?>\n${svg}`;
}


draw();
